import { useState, useCallback, useMemo, FC } from "react";
import {
  MantineReactTable,
  MRT_Table as MRTTable,
  useMantineReactTable,
} from "mantine-react-table";
import {
  LivenessWebVerifyStatusEnum,
  RegtankLivenessFHStatusEnum,
  RegtankOnboardingFHStatusEnum,
  RegtankOnboardingStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { Modal, Text, Card, CardSection } from "@mantine/core";
import { useStyles } from "./Regtank.styles";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import type {
  RegtankProps,
  RegtankData,
  RegtankHistoryData,
  RegtankLivenessData,
  RegtankLivenessHistoryData,
} from "./Regtank.types";
import { useRegtankModals } from "./hooks/useRegtankModals";
import { useDocumentViewer } from "./hooks/useDocumentViewer";
import { useReattemptApproval } from "./hooks/useReattemptApproval";
import { useRegtankData } from "./hooks/useRegtankData";
import { MediaViewerModal } from "./components/MediaViewerModal";
import { UserDataCard } from "./components/UserDataCard";
import { ApprovalRejectionSection } from "./components/ApprovalRejectionSection";
import { useIndividualOnboardingColumns } from "./columns/individualOnboardingColumns";
import { useOnboardingColumns } from "./columns/columns";
import { useVerificationOnboardingColumns } from "./columns/verificationOnboardingColumns";
import { useIndividualLivenessColumns } from "./columns/individualLivenessColumns";
import { useLivenessColumns } from "./columns/livenessColumns";
import { useLivenessHistoryColumns } from "./columns/livenessHistoryColumns";
import * as regtankHelpers from "./utils/regtankHelpers";
import { sortRegtankRequestsByExpiry } from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";
import {
  resultLivenessChecking,
  resultOnboardingChecking,
} from "../../../Utils/getRegtankDetails";
import {
  VERIFICATION_SECTION_TITLES,
} from "./Regtank.constants";

const Regtank: FC<RegtankProps> = ({ orgData, userData }) => {
  const { classes } = useStyles();
  const { token } = useSelector((state: RootState) => state.auth);

  // State for data updates
  const [updatedFinalData, setUpdatedFinalData] = useState<RegtankData[]>([]);
  const [updatedLivenessFinalData, setUpdatedLivenessFinalData] = useState<
    RegtankLivenessData[]
  >([]);

  // Custom hooks for modal management
  const modals = useRegtankModals();

  // Custom hook for data processing
  const dataHook = useRegtankData({
    userData,
    orgData,
    updatedFinalData,
    updatedLivenessFinalData,
  });

  // Custom hook for document viewer
  const documentViewer = useDocumentViewer({
    token,
    verificationHistoryData: dataHook.verificationHistoryData,
    onOpenDocument: modals.openDocument,
    onCloseDocument: modals.closeDocument,
    onOpenImageModal: modals.openImageModal,
  });

  // Destructure all needed values from dataHook
  const {
    isLoading,
    finalData,
    livenessFinalData,
    verificationHistoryData,
    livenessHistoryData,
    regtankFHResultTotal,
    livenessFHResultTotal,
    regtankOnboardingPerUserRowData,
    setRegtankOnboardingPerUserRowData,
    regtankLivenessCardData,
    setRegtankLivenessCardData,
  } = dataHook;

  // Custom hook for reattempt approval (placed after destructuring to access setters)
  const reattemptApproval = useReattemptApproval({
    token,
    orgData,
    userData,
    onCloseApprovalModal: modals.closeVerificationAppr,
    onCloseRejectionModal: modals.closeVerificationReject,
    convertToUtcTwelveHours: documentViewer.convertToUtcTwelveHours,
    verificationHistoryData,
    setVerificationHistoryData: dataHook.setVerificationHistoryData,
    livenessHistoryData,
    setLivenessHistoryData: dataHook.setLivenessHistoryData,
    setRegtankOnboardingPerUserRowData,
    setRegtankLivenessCardData,
    setFinalData: dataHook.setFinalData,
    setUpdatedFinalData,
    setLivenessFinalData: dataHook.setLivenessFinalData,
    setUpdatedLivenessFinalData,
    finalData,
    livenessFinalData,
  });

  // Destructure values from reattemptApproval hook
  const {
    remark,
    setRemark,
    remarkApprButtonDisabled,
    remarkRejectButtonDisabled,
    nameRole,
    setNameRole,
    setUserId,
    setRequestIdReattempt,
    setSessionIdReattempt,
    handleOnboardingApproveReattempt,
    handleOnboardingRejectReattempt,
    handleLivenessApproveReattempt,
    handleLivenessRejectReattempt,
  } = reattemptApproval;

  // Destructure values from documentViewer hook
  const {
    titleDocument,
    imageDocument,
    videoDocument,
    timestampDoc,
    titleLivenessDocument,
    imageLiveness,
    videoLiveness,
    timestampLiveness,
    handleClick,
    handleViewPhotoVideoLiveness,
  } = documentViewer;

  /**
   * Bound helper functions object
   * All helper functions are pre-bound with their dependencies
   */
  const boundHelpers = useMemo(
    () => ({
      getRegtankHistoryVerificationResult: (
        requestId: string,
        status: RegtankOnboardingStatusEnum
      ) =>
        regtankHelpers.getRegtankHistoryVerificationResult(
          requestId,
          status,
          regtankFHResultTotal
        ),
      getRegtankVerificationResultColor: (
        requestId: string,
        status: RegtankOnboardingStatusEnum
      ) =>
        regtankHelpers.getRegtankVerificationResultColor(
          requestId,
          status,
          regtankFHResultTotal
        ),
      getReattemptApprovalColor: (status: string) =>
        regtankHelpers.getReattemptApprovalColor(status),
      handleViewDocumentFront: (requestId: string): boolean =>
        regtankHelpers.shouldDisableDocumentFront(
          requestId,
          regtankFHResultTotal
        ),
      handleViewDocumentBack: (requestId: string): boolean =>
        regtankHelpers.shouldDisableDocumentBack(
          requestId,
          regtankFHResultTotal
        ),
      handleViewLiveness: (requestId: string, submittedDate: string): boolean =>
        regtankHelpers.shouldDisableLiveness(
          requestId,
          regtankFHResultTotal,
          livenessFHResultTotal,
          submittedDate
        ),
      getNewSubmittedOn: (requestId?: string, sessionId?: string) =>
        regtankHelpers.hasSubmittedOn(
          requestId,
          sessionId,
          regtankFHResultTotal,
          livenessFHResultTotal
        ),
      getNewFailureReason: (
        reason: string,
        requestId?: string,
        sessionId?: string
      ) =>
        regtankHelpers.getNewFailureReason(
          reason,
          requestId,
          sessionId,
          regtankFHResultTotal,
          livenessFHResultTotal
        ),
      getNewReviewedOn: (
        reviewedOn: string,
        requestId?: string,
        sessionId?: string
      ) =>
        regtankHelpers.getNewReviewedOn(
          reviewedOn,
          requestId,
          sessionId,
          regtankFHResultTotal,
          livenessFHResultTotal,
          verificationHistoryData,
          livenessHistoryData
        ),
      getNewRemark: (remark: string, requestId?: string, sessionId?: string) =>
        regtankHelpers.getNewRemark(
          remark,
          requestId,
          sessionId,
          regtankFHResultTotal,
          livenessFHResultTotal,
          verificationHistoryData,
          livenessHistoryData
        ),
      getNewReattemptApproval: (
        reattemptApproval: string,
        requestId?: string,
        sessionId?: string
      ) =>
        regtankHelpers.getNewReattemptApproval(
          reattemptApproval,
          requestId,
          sessionId,
          regtankFHResultTotal,
          livenessFHResultTotal,
          verificationHistoryData,
          livenessHistoryData
        ),
      getLivenessHistoryVerificationResult: (
        sessionId: string,
        status: LivenessWebVerifyStatusEnum
      ) =>
        regtankHelpers.getLivenessHistoryVerificationResult(
          sessionId,
          status,
          livenessFHResultTotal
        ),
      getLivenessVerificationResultColor: (
        sessionId: string,
        status: LivenessWebVerifyStatusEnum
      ) =>
        regtankHelpers.getLivenessVerificationResultColor(
          sessionId,
          status,
          livenessFHResultTotal
        ),
    }),
    [
      regtankFHResultTotal,
      livenessFHResultTotal,
      verificationHistoryData,
      livenessHistoryData,
    ]
  );

  // Destructure for backward compatibility with existing code
  const {
    getRegtankHistoryVerificationResult,
    getRegtankVerificationResultColor,
    getReattemptApprovalColor,
    handleViewDocumentFront,
    handleViewDocumentBack,
    handleViewLiveness,
    getNewSubmittedOn,
    getNewFailureReason,
    getNewReviewedOn,
    getNewRemark,
    getNewReattemptApproval,
    getLivenessHistoryVerificationResult,
    getLivenessVerificationResultColor,
  } = boundHelpers;

  /**
   * Generic history data processor
   * Consolidates duplicate logic between handleView and handleLivenessView
   */
  const processHistoryData = useCallback(
    <
      TData extends {
        userId: number;
        name?: string;
        userRole?: string;
        lastAttemptDate: string;
      },
    >(
      inputData: TData,
      config: {
        rawDataKey: "regtankRequest" | "regtankLiveness";
        // Data extraction
        getUserHistory: (
          resultChecking: (resultOnboardingChecking | resultLivenessChecking)[],
          user: any
        ) => any[] | undefined;
        // State setters
        setCardData: (data: TData) => void;
        setHistoryData: (data: any[]) => void;
        setIdReattempt: (id: string) => void;
        clearHistoryData: () => void;
        // Match data source
        getMatchResults: () => any[];
        matchIdField: string;
        // Field mapper
        mapHistoryItem: (item: any, userId: number) => any;
        // Modal opener
        openModal: () => void;
        // Optional pending status check
        pendingStatusValue?: string;
      }
    ) => {
      config.clearHistoryData();
      setNameRole(
        (inputData.name ?? "") + " (" + (inputData.userRole ?? "") + ")"
      );
      setUserId(inputData.userId.toString());

      const user = userData?.find((x) => x.id === inputData.userId);
      if (!user) {
        // User not found - should not happen but handle gracefully
        config.setCardData(inputData);
        config.openModal();
        return;
      }

      let processedHistory: any = [];
      if (config.rawDataKey === "regtankRequest") {
        processedHistory = config.getUserHistory(regtankFHResultTotal, user);
      } else if (config.rawDataKey === "regtankLiveness") {
        processedHistory = config.getUserHistory(livenessFHResultTotal, user);
      }
      const historyWithUserId = processedHistory?.map((r: any) => ({
        ...r,
        userId: user.id,
      }));

      let sortedData: any;
      let finalisedHistoryData: any[] = [];
      let dataToBeHidden: { requestId: string }[] = [];
      if (historyWithUserId && historyWithUserId.length > 0) {
        sortedData = [...historyWithUserId];
        sortedData = sortRegtankRequestsByExpiry(historyWithUserId);
      }

      if (sortedData) {
        if (inputData.lastAttemptDate === "" && sortedData.length > 1) {
          const lastSubmittedDate =
            sortedData[sortedData.length - 1].submittedDate;
          const newData = {
            ...inputData,
            lastAttemptDate:
              lastSubmittedDate !== null
                ? documentViewer.convertToUtcTwelveHours(lastSubmittedDate)
                : "",
          };
          config.setCardData(newData);
        }

        if (sortedData.length > 0) {
          config.setCardData(inputData);
          config.setIdReattempt(sortedData[0][config.matchIdField]);

          for (const item of sortedData) {
            let historyItem = config.mapHistoryItem(item, user.id);
            const matchResults = config.getMatchResults();
            const hasMatch = matchResults.find(
              (x: any) =>
                x[
                  `match${
                    config.matchIdField.charAt(0).toUpperCase() +
                    config.matchIdField.slice(1)
                  }`
                ] === historyItem[config.matchIdField]
            );
            // Check if there are any "new" fields with "-" value
            const hasDashFields =
              hasMatch &&
              Object.keys(hasMatch).some(
                (key) => key.startsWith("new") && hasMatch[key] === "-"
              );

            if (hasDashFields) {
              // Merge only the "new" fields that are "-" into historyItem
              historyItem = {
                ...historyItem,
                ...Object.keys(hasMatch)
                  .filter(
                    (key) => key.startsWith("new") && hasMatch[key] === "-"
                  )
                  .reduce((acc: any, key) => {
                    const fieldName = key.replace(/^new/, "");
                    const targetKey =
                      fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
                    acc[targetKey] = hasMatch[key];
                    return acc;
                  }, {}),
              };
            }
            if (
              (historyItem.verificationResult === null ||
                historyItem.verificationResult === "PROCESSING") &&
              new Date(historyItem.expiredDate) < new Date()
            ) {
              console.warn(
                `Filtering expired Regtank record: ${historyItem.id}`
              );
              continue;
            }
            finalisedHistoryData.push(historyItem);

            if (
              hasMatch?.hiddenVerificationHistoryData ||
              hasMatch?.hiddenLivenessHistoryData
            ) {
              dataToBeHidden.push(hasMatch.matchRequestId);
            }
          }

          finalisedHistoryData = finalisedHistoryData.filter(
            (x) => !dataToBeHidden.includes(x.requestId)
          );
          config.setHistoryData(finalisedHistoryData);
        }
      } else if (config.pendingStatusValue) {
        // Handle pending verification case
        const newData = {
          ...inputData,
          lastAttemptDate: "",
        };
        config.setCardData(newData);
      } else {
        config.setCardData(inputData);
      }

      config.openModal();
    },
    [
      userData,
      setNameRole,
      setUserId,
      documentViewer,
      regtankFHResultTotal,
      livenessFHResultTotal,
    ]
  );

  const handleView = useCallback(
    (dataCheck: RegtankData) => {
      processHistoryData(dataCheck, {
        rawDataKey: "regtankRequest",
        getUserHistory: (regtankFHResultTotal, user) => {
          return (
            user?.regtankRequest?.map((request: any) => {
              const matchResult = regtankFHResultTotal.find(
                (x: any) => x.matchRequestId === request.requestId
              );
              return {
                ...request,
                ...matchResult,
              };
            }) || []
          );
        },
        setCardData: setRegtankOnboardingPerUserRowData,
        setHistoryData: dataHook.setVerificationHistoryData,
        setIdReattempt: setRequestIdReattempt,
        clearHistoryData: () => dataHook.setVerificationHistoryData([]),
        getMatchResults: () => dataHook.regtankFHResultTotal,
        matchIdField: "requestId",
        mapHistoryItem: (data, userId) => ({
          name: data.name,
          requestId: data.requestId,
          linkSentOn: documentViewer.convertToUtcTwelveHours(data.createdDate),
          submittedOn:
            data.submittedDate !== null
              ? documentViewer.convertToUtcTwelveHours(data.submittedDate)
              : "",
          reviewedOn:
            data.reviewedDate !== null
              ? documentViewer.convertToUtcTwelveHours(data.reviewedDate)
              : "",
          verificationResult: data.onboardingStatus,
          reason: data.reason,
          remark: data.remark,
          reattemptApproval: data.reattemptApproval,
          expiredDate: data.expiredDate,
          userId: data.userId,
          documentIdSubmittedDate:
            data.documentIdSubmittedDate !== null
              ? documentViewer.convertToUtcTwelveHours(
                  data.documentIdSubmittedDate
                )
              : "",
        }),
        openModal: modals.openRegtankOnboarding,
        pendingStatusValue:
          dataCheck.onboardingStatus ===
          RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION
            ? RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION
            : undefined,
      });
    },
    [
      processHistoryData,
      dataHook,
      setRequestIdReattempt,
      documentViewer,
      setRegtankOnboardingPerUserRowData,
      modals,
    ]
  );

  const handleLivenessView = useCallback(
    (data: RegtankLivenessData) => {
      processHistoryData(data, {
        rawDataKey: "regtankLiveness",
        getUserHistory: (livenessFHResultTotal, user) => {
          return (
            user?.regtankLiveness?.map((liveness: any) => {
              const matchResult = livenessFHResultTotal.find(
                (x: any) => x.matchSessionId === liveness.sessionId
              );
              return {
                ...liveness,
                ...matchResult,
              };
            }) || []
          );
        },
        setCardData: setRegtankLivenessCardData,
        setHistoryData: dataHook.setLivenessHistoryData,
        setIdReattempt: setSessionIdReattempt,
        clearHistoryData: () => dataHook.setLivenessHistoryData([]),
        getMatchResults: () => dataHook.livenessFHResultTotal,
        matchIdField: "sessionId",
        mapHistoryItem: (item, userId) => ({
          name: item.name,
          sessionId: item.sessionId,
          linkSentOn: documentViewer.convertToUtcTwelveHours(item.createdAt),
          reviewedOn:
            item.reviewedDate !== null
              ? documentViewer.convertToUtcTwelveHours(item.reviewedDate)
              : "",
          failureReason: item.reason,
          remark: item.remark,
          reattemptApproval: item.reattemptApproval,
          agreement: item.agreementName,
          expiredDate:
            item.expiredDate !== null
              ? documentViewer.convertToUtcTwelveHours(item.expiredDate)
              : "",
          verificationResult: item.livenessStatus,
          submittedDate:
            item.submittedDate !== null
              ? documentViewer.convertToUtcTwelveHours(item.submittedDate)
              : "",
          confidence: item.confidence !== null ? item.confidence : undefined,
          clientSessionId: item.clientSessionId,
          userId: userId.toString(),
        }),
        openModal: modals.openLiveness,
      });
    },
    [
      processHistoryData,
      dataHook,
      setSessionIdReattempt,
      documentViewer,
      setRegtankLivenessCardData,
      modals,
    ]
  );

  const individualOnboardingColumns = useIndividualOnboardingColumns();

  const columns = useOnboardingColumns();

  const verificationOnboardingColumns = useVerificationOnboardingColumns({
    getNewSubmittedOn,
    getNewReviewedOn,
    handleViewDocumentFront,
    handleViewDocumentBack,
    handleViewLiveness,
    handleClick,
    getRegtankHistoryVerificationResult,
    getRegtankVerificationResultColor,
    getNewFailureReason,
    getNewRemark,
    getNewReattemptApproval,
    getReattemptApprovalColor,
  });

  const individualLivenessColumns = useIndividualLivenessColumns();

  const livenessColumns = useLivenessColumns();

  const livenessHistoryColumns = useLivenessHistoryColumns({
    getNewSubmittedOn,
    getNewReviewedOn,
    handleViewLiveness,
    handleViewPhotoVideoLiveness,
    getLivenessHistoryVerificationResult,
    getLivenessVerificationResultColor,
    getNewFailureReason,
    getNewRemark,
    getNewReattemptApproval,
    getReattemptApprovalColor,
  });

  /**
   * Generic table configuration factory
   */
  const createTableConfig = useCallback(
    <TData,>(
      columns: any,
      data: TData[],
      options?: {
        enableTopToolbar?: boolean;
        enableSorting?: boolean;
        onRowClick?: (row: TData) => void;
      }
    ) => ({
      enableColumnActions: false,
      enableSorting: options?.enableSorting ?? false,
      paginationDisplayMode: "pages" as const,
      state: { isLoading },
      columns,
      data: data ?? [],
      initialState: { density: "xs" as const },
      mantineTableProps: {
        sx: { maxHeight: "500px" },
        withBorder: true,
        withColumnBorders: true,
      },
      enableStickyHeader: true,
      ...(options?.onRowClick && { positionActionsColumn: "last" as const }),
      enableTopToolbar: options?.enableTopToolbar ?? true,
      ...(options?.onRowClick && {
        mantineTableBodyRowProps: ({ row }: any) => ({
          onClick: () => options.onRowClick!(row?.original),
        }),
      }),
    }),
    [isLoading]
  );

  const table = useMantineReactTable<RegtankData>(
    createTableConfig(
      orgData?.companyName !== null ? columns : individualOnboardingColumns,
      finalData,
      { onRowClick: handleView }
    )
  );

  const verficationTable = useMantineReactTable<RegtankHistoryData>(
    createTableConfig(verificationOnboardingColumns, verificationHistoryData, {
      enableTopToolbar: false,
    })
  );

  const livenessTable = useMantineReactTable<RegtankLivenessData>(
    createTableConfig(
      orgData?.companyName !== null
        ? livenessColumns
        : individualLivenessColumns,
      livenessFinalData,
      { onRowClick: handleLivenessView }
    )
  );

  const livenessHistoryTable = useMantineReactTable<RegtankLivenessHistoryData>(
    createTableConfig(livenessHistoryColumns, livenessHistoryData, {
      enableTopToolbar: false,
    })
  );

  return (
    <>
      {orgData?.companyName === null &&
        finalData.length > 0 &&
        livenessFinalData.length > 0 && (
          <>
            <Text className={classes.subtext}>{finalData?.[0].name}</Text>
            <Text className={classes.subSmallText}>
              {finalData?.[0].userRole}
            </Text>
            <Text className={classes.subSmallText}>{finalData?.[0].email}</Text>
            <Card
              withBorder
              shadow="sm"
              radius="md"
              className={classes.card}
              padding="md"
            >
              <CardSection inheritPadding py="xs">
                <Text className={classes.instructionHeading}>
                  {VERIFICATION_SECTION_TITLES.onboardingVerification}
                </Text>
                <MRTTable table={table} />
                <Text className={classes.instructionHeading2}>
                  {VERIFICATION_SECTION_TITLES.independentLiveness}
                </Text>
                <MRTTable table={livenessTable} />
              </CardSection>
            </Card>
          </>
        )}
      {orgData?.companyName !== null && (
        <>
          <Text className={classes.instructionHeading2}>
            {VERIFICATION_SECTION_TITLES.onboardingVerification}
          </Text>
          <MRTTable table={table} />
          <Text className={classes.instructionHeading2}>
            {VERIFICATION_SECTION_TITLES.independentLiveness}
          </Text>
          <MRTTable table={livenessTable} />
        </>
      )}
      <Modal
        opened={modals.openedRegtankOnboarding}
        onClose={() => {
          setRemark("");
          modals.closeRegtankOnboarding();
        }}
        size="auto"
      >
        {regtankOnboardingPerUserRowData && (
          <UserDataCard
            userData={regtankOnboardingPerUserRowData}
            type="onboarding"
          />
        )}
        <div className={classes.div}>
          <Text className={classes.instructionHeading}>
            Verification History
          </Text>
          <MantineReactTable table={verficationTable} />
          <MediaViewerModal
            opened={modals.openedDocument}
            onClose={modals.closeDocument}
            title={titleDocument}
            imageUrl={imageDocument}
            videoUrl={videoDocument}
            timestamp={timestampDoc}
          />
        </div>
        <ApprovalRejectionSection
          showSection={
            regtankOnboardingPerUserRowData?.onboardingStatus ===
              RegtankOnboardingFHStatusEnum.PENDING_REVIEW &&
            regtankOnboardingPerUserRowData?.userRole !== "Removed User"
          }
          remark={remark}
          onRemarkChange={setRemark}
          nameRole={nameRole}
          onApprove={handleOnboardingApproveReattempt}
          onReject={handleOnboardingRejectReattempt}
          approveButtonDisabled={remarkApprButtonDisabled}
          rejectButtonDisabled={remarkRejectButtonDisabled}
          modals={modals}
        />
      </Modal>
      <Modal
        opened={modals.openedLiveness}
        onClose={() => {
          setRemark("");
          modals.closeLiveness();
        }}
        size="auto"
      >
        {regtankLivenessCardData && (
          <UserDataCard userData={regtankLivenessCardData} type="liveness" />
        )}
        <div className={classes.div}>
          <Text className={classes.instructionHeading}>
            Verification History
          </Text>
          <MantineReactTable table={livenessHistoryTable} />
          <MediaViewerModal
            opened={modals.openedImageModal}
            onClose={modals.closeImageModal}
            title={titleLivenessDocument}
            imageUrl={imageLiveness}
            videoUrl={videoLiveness}
            timestamp={timestampLiveness}
          />
        </div>
        <ApprovalRejectionSection
          showSection={
            regtankLivenessCardData?.verificationStatus ===
            RegtankLivenessFHStatusEnum.PENDING_REVIEW
          }
          remark={remark}
          onRemarkChange={setRemark}
          nameRole={nameRole}
          onApprove={handleLivenessApproveReattempt}
          onReject={handleLivenessRejectReattempt}
          approveButtonDisabled={remarkApprButtonDisabled}
          rejectButtonDisabled={remarkRejectButtonDisabled}
          modals={modals}
        />
      </Modal>
    </>
  );
};

export default Regtank;
